import React from 'react';
import { Instagram, Facebook, Heart, Store, MessageCircle } from 'lucide-react';

interface AboutUsSectionProps {
    tenant?: {
        about_us?: string | null;
        instagram?: string | null;
        facebook?: string | null;
        tiktok?: string | null;
        name?: string;
        banner?: string | null;
        logotype?: string | null;
        whatsapp?: string | null;
    };
}

// URL hardcodeada de fallback solo para la descripción si es nula
const COMANDAYA_SOCIAL = {
    about: 'Plataforma de pedidos digitales para bares y restaurantes. Agiliza tu experiencia gastronómica con pedidos desde tu mesa, take-away y delivery.',
};

export const AboutUsSection: React.FC<AboutUsSectionProps> = ({ tenant }) => {
    const defaultName = "ComandaYa";
    const tenantName = tenant?.name || defaultName;

    // Usar datos del tenant o fallbacks solo para el texto
    const aboutUsText = tenant?.about_us || COMANDAYA_SOCIAL.about;

    // Parseo seguro de redes sociales, sin usar fallbacks (si no existe, queda en nulo)
    const instagramUrl = tenant?.instagram
        ? (tenant.instagram.includes('http') ? tenant.instagram : `https://instagram.com/${tenant.instagram.replace('@', '')}`)
        : null;
    const facebookUrl = tenant?.facebook
        ? (tenant.facebook.includes('http') ? tenant.facebook : `https://facebook.com/${tenant.facebook}`)
        : null;
    const tiktokUrl = tenant?.tiktok
        ? (tenant.tiktok.includes('http') ? tenant.tiktok : `https://tiktok.com/@${tenant.tiktok.replace('@', '')}`)
        : null;
    const whatsappUrl = tenant?.whatsapp
        ? (tenant.whatsapp.includes('wa.me') || tenant.whatsapp.includes('http') ? tenant.whatsapp : `https://wa.me/${tenant.whatsapp.replace(/\D/g, '')}`)
        : null;

    const bannerUrl = tenant?.banner;

    return (
        <div className="mt-12 space-y-6 px-2 pb-10">
            {/* Title Block with Glow */}
            <div className="flex flex-col items-center justify-center text-center space-y-3 relative z-10 mb-8">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-accent-500/20 dark:bg-accent-500/10 blur-3xl rounded-full z-0 pointer-events-none" style={{ backgroundColor: 'var(--color-accent-500)', opacity: 0.15 }} />
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 backdrop-blur-sm relative z-10 shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
                    <span className="text-[10px] font-black text-green-600 dark:text-green-400 tracking-widest uppercase">Recibiendo Pedidos</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-black text-gray-900 dark:text-white tracking-tight relative z-10">{tenantName}</h2>
            </div>

            {/* Bento Grid layout */}
            <div className="grid grid-cols-2 gap-3">

                {/* Main Info Block */}
                <div className="col-span-2 relative overflow-hidden rounded-[2rem] bg-white/60 dark:bg-gray-800/40 p-6 backdrop-blur-2xl border border-gray-100 dark:border-white/5 shadow-xl">
                    <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-gradient-to-br from-primary-400/20 to-accent-500/20 blur-3xl rounded-full pointer-events-none" style={{ background: 'linear-gradient(135deg, var(--color-primary-400), var(--color-accent-500))', opacity: 0.2 }} />
                    <div className="relative z-10">
                        <div className="w-12 h-12 bg-gray-900 dark:bg-white/10 rounded-2xl flex items-center justify-center mb-4 shadow-md relative group border border-gray-800 dark:border-white/20">
                            <Store className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300" />
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 font-medium leading-relaxed text-[14px] whitespace-pre-line">
                            {aboutUsText}
                        </p>
                    </div>
                </div>

                {/* WhatsApp Block */}
                {whatsappUrl && (
                    <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative overflow-hidden rounded-[1.5rem] flex flex-col items-center justify-center py-6 px-2 bg-white/60 dark:bg-gray-800/40 border border-gray-100 dark:border-white/5 backdrop-blur-2xl shadow-lg group hover:scale-[1.03] active:scale-95 transition-all duration-300"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-emerald-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-2 shadow-lg shadow-green-500/30 group-hover:-rotate-6 group-hover:scale-110 transition-transform duration-300">
                            <MessageCircle className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200">WhatsApp</span>
                    </a>
                )}

                {/* Instagram Block */}
                {instagramUrl && (
                    <a
                        href={instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative overflow-hidden rounded-[1.5rem] flex flex-col items-center justify-center py-6 px-2 bg-white/60 dark:bg-gray-800/40 border border-gray-100 dark:border-white/5 backdrop-blur-2xl shadow-lg group hover:scale-[1.03] active:scale-95 transition-all duration-300"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500 rounded-2xl flex items-center justify-center mb-2 shadow-lg shadow-pink-500/30 group-hover:-rotate-6 group-hover:scale-110 transition-transform duration-300">
                            <Instagram className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200">Instagram</span>
                    </a>
                )}

                {/* Facebook Block */}
                {facebookUrl && (
                    <a
                        href={facebookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative overflow-hidden rounded-[1.5rem] flex flex-col items-center justify-center py-6 px-2 bg-white/60 dark:bg-gray-800/40 border border-gray-100 dark:border-white/5 backdrop-blur-2xl shadow-lg group hover:scale-[1.03] active:scale-95 transition-all duration-300"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mb-2 shadow-lg shadow-blue-500/30 group-hover:rotate-6 group-hover:scale-110 transition-transform duration-300">
                            <Facebook className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200">Facebook</span>
                    </a>
                )}

                {/* TikTok Block */}
                {tiktokUrl && (
                    <a
                        href={tiktokUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative overflow-hidden rounded-[1.5rem] flex flex-col items-center justify-center py-6 px-2 bg-white/60 dark:bg-gray-800/40 border border-gray-100 dark:border-white/5 backdrop-blur-2xl shadow-lg group hover:scale-[1.03] active:scale-95 transition-all duration-300"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-600/10 via-gray-800/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 rounded-2xl flex items-center justify-center mb-2 shadow-lg shadow-black/30 group-hover:rotate-6 group-hover:scale-110 transition-transform duration-300">
                            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.68a8.17 8.17 0 004.77 1.52V6.69a4.85 4.85 0 01-1-.06z" />
                            </svg>
                        </div>
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200">TikTok</span>
                    </a>
                )}
            </div>

            {/* Footer Powered By */}
            <div className="flex flex-col items-center justify-center gap-3 pt-8 pb-4 opacity-70">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <a href="https://www.argonglobal.tech"  target="_blank" rel="noopener noreferrer" className="text-[10px] font-black tracking-widest uppercase text-gray-500 dark:text-gray-400">Kiero - Argon Global Tech</a>
                </div>
                <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                    {/* <span className="text-[11px] font-medium">Desarrollado por</span>
                    <span className="text-xs font-black text-gray-500 dark:text-gray-300">Quinoto & Argon Global Tech</span> */}
                </div>
            </div>
        </div>
    );
};